import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal, WritableSignal, Signal, computed } from '@angular/core';
import { catchError, Observable, of, tap, timeout } from 'rxjs';
import { AppConfig } from '@core/models';
import { AVAILABLE_CONFIGS, environment } from '@env/environment';

@Injectable()
export class AppConfigService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  // Current host name
  private readonly _currentHost = this.document.location.hostname;
  // Private signal for the config (to protect it from outside changes)
  private readonly _config: WritableSignal<AppConfig> = signal(environment.apiSettings);
  // Public signal for the config (to be used outside the service)
  public readonly currentConfig: Signal<AppConfig> = this._config.asReadonly();
  
  // Computed signals for the config
  public readonly host = computed(() => this.currentConfig().host);
  public readonly code = computed(() => this.currentConfig().code);
  public readonly currency = computed(() => this.currentConfig().currency ?? 'USD');

  /**
   * Load the configuration from the local server
   * If the configuration is not found (error in the server), return the default environment config
   * @returns Observable of the configuration
   */
  loadConfig(): Observable<AppConfig> {
    return this.http.get<AppConfig>(`${environment.localServerUrl}/config/v2`).pipe(
      timeout(5000),
      catchError((error) => {
        console.error('Error loading config:', error);
        //if error, return the default environment config
        return of(this.getDefaultConfig() ?? null);
      }),
      tap((config) => this.setCurrentConfig(config))
    );
  }

  /**
   * Set the configuration
   * The components using the signals will be automatically notified
   * @param newConfig - The new configuration
   */
  setCurrentConfig(newConfig: AppConfig): void {
    this._config.set(newConfig);
  }

  getConfigsByCurrentHost(): AppConfig[] {
    if (environment.env === "DEV") {
      return AVAILABLE_CONFIGS;
    }

    const culturespacesHosts = ["museum-dev.guestup.com", "museum-test.guestup.com", "event-culturespaces.guestup.com"];
    const cartierHosts = ["fc-dev.guestup.com", "fc-test.guestup.com", "event-fondationcartier.guestup.com"];
    
    if (culturespacesHosts.includes(this._currentHost)) {
      return AVAILABLE_CONFIGS.filter((config) => config.code !== "FC");
    }

    if (cartierHosts.includes(this._currentHost)) {
      return AVAILABLE_CONFIGS.filter((config) => config.code === "FC");
    }

    return AVAILABLE_CONFIGS;
  }

  // return the default environment config based on the host name
  private getDefaultConfig(): AppConfig {
    const configs = this.getConfigsByCurrentHost();
    return configs[0];
  }
  
}